import type {
  RoiMetricsSettingsRequest,
  RoiMetricsSettingsResponse,
  StatisticsResponse,
  WeeklyStatisticsDetail,
} from "@/api";
import { StatisticsDateFilters } from "@/models/statistics";
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from "date-fns";

/**
 * ROI Metrics - Domain logic for calculating ROI metrics
 *
 * This model contains:
 * - Assumptions (constants) for time savings calculations
 * - Calculation functions for each metric
 * - Tooltip descriptions for each metric
 */

// ===== Assumptions (Constants) =====
export const ROI_ASSUMPTIONS = {
  /** Minutes saved per resume screening */
  MINUTES_PER_RESUME_SCREENING: 5,
  /** Minutes saved per chat message */
  MINUTES_PER_CHAT_MESSAGE: 3,
  /** Multiplier for phone interview time (includes pre/post work) */
  PHONE_INTERVIEW_TIME_MULTIPLIER: 1.5,
  /** Minutes saved per interview evaluation report */
  MINUTES_PER_EVALUATION_REPORT: 20,
  /** Default recruiter hourly rate in USD (fallback) */
  DEFAULT_RECRUITER_HOURLY_RATE_USD: 34,
  /** Annual working hours for hourly ↔ yearly conversion */
  ANNUAL_WORKING_HOURS: 2080,
} as const;

/** Count weekdays (Mon–Fri) from start to end inclusive. */
const countBusinessDays = (start: Date, end: Date): number => {
  const s = new Date(start);
  s.setHours(0, 0, 0, 0);
  const e = new Date(end);
  e.setHours(0, 0, 0, 0);

  if (s > e) return 0;

  let count = 0;
  const cursor = new Date(s);
  while (cursor <= e) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const isValidDate = (date: Date | undefined): date is Date =>
  date instanceof Date && !Number.isNaN(date.getTime());

/**
 * Get FTE work hours for a given period based on business days × 8.
 *
 * When orgStartDate is provided the period start is clamped to
 * max(periodStart, orgStartDate) so the denominator reflects only
 * the time the organization has actually been active.
 */
export const getProratedHoursPerFte = (
  period: StatisticsDateFilters,
  now: Date = new Date(),
  orgStartDate?: Date,
): number => {
  let periodStart: Date;
  let periodEnd: Date;

  switch (period) {
    case StatisticsDateFilters.today:
      periodStart = startOfDay(now);
      periodEnd = now;
      break;
    case StatisticsDateFilters.yesterday: {
      const prev = subDays(now, 1);
      periodStart = startOfDay(prev);
      periodEnd = prev;
      break;
    }
    case StatisticsDateFilters.this_week:
      periodStart = startOfWeek(now);
      periodEnd = now;
      break;
    case StatisticsDateFilters.last_week: {
      const prev = subWeeks(now, 1);
      periodStart = startOfWeek(prev);
      periodEnd = endOfWeek(prev);
      break;
    }
    case StatisticsDateFilters.this_month:
      periodStart = startOfMonth(now);
      periodEnd = now;
      break;
    case StatisticsDateFilters.last_month: {
      const prev = subMonths(now, 1);
      periodStart = startOfMonth(prev);
      periodEnd = endOfMonth(prev);
      break;
    }
    case StatisticsDateFilters.this_quarter:
      periodStart = startOfQuarter(now);
      periodEnd = now;
      break;
    case StatisticsDateFilters.last_quarter: {
      const prev = subQuarters(now, 1);
      periodStart = startOfQuarter(prev);
      periodEnd = endOfQuarter(prev);
      break;
    }
    case StatisticsDateFilters.this_year:
      periodStart = startOfYear(now);
      periodEnd = now;
      break;
    case StatisticsDateFilters.last_year: {
      const prev = subYears(now, 1);
      periodStart = startOfYear(prev);
      periodEnd = endOfYear(prev);
      break;
    }
    case StatisticsDateFilters.all:
    default:
      if (isValidDate(orgStartDate)) {
        periodStart = startOfDay(orgStartDate);
        periodEnd = now;
      } else {
        return ROI_ASSUMPTIONS.ANNUAL_WORKING_HOURS;
      }
      break;
  }

  if (isValidDate(orgStartDate) && orgStartDate > periodStart) {
    periodStart = startOfDay(orgStartDate);
  }

  if (periodStart > periodEnd) return 8;

  return Math.max(countBusinessDays(periodStart, periodEnd) * 8, 8);
};

// ===== Tooltip Descriptions =====
export const ROI_TOOLTIPS = {
  hiring_cost_saved: {
    title: "Hiring cost saved",
    description:
      "Estimated cost avoided based on reduced manual effort and faster hiring, using your baseline inputs.",
  },
  manual_time_eliminated: {
    title: "Hiring time saved",
    description:
      "Total time saved by automating screening, scheduling, and follow-ups across all active roles.",
  },
  hiring_capacity: {
    title: "Recruiter workload covered",
    description:
      "The percentage of a full-time recruiter's workload handled by the AI agent during this period.",
  },
  hired_count: {
    title: "Hires through agent",
    description:
      "Number of candidates successfully hired through AI-led screening and coordination.",
  },
  offer_count: {
    title: "Offers through agent",
    description:
      "Number of AI-screened candidates the customer advanced to an offer during the selected period. Counts offers recorded here and observed moves to an ATS stage configured as an offer.",
  },
  time_to_offer: {
    title: "Time to offer",
    description:
      "Average time from application submission to offers reached during the selected period. Measured from offers recorded here and observed moves to an ATS stage configured as an offer.",
  },
  time_to_hire: {
    title: "Time to hire",
    description:
      "Average time from application submission to hired status during the selected period.",
  },
  time_saved_over_time: {
    title: "Time saved over time",
    description:
      "Cumulative time saved across all roles over the selected period. The % change shows how this total compares to the previous week.",
  },
  cost_savings_over_time: {
    title: "Cost savings over time",
    description:
      "Estimated cumulative cost savings over the selected period, based on time saved and baseline assumptions. The % change shows how this estimate compares to the previous week.",
  },
} as const;

// ===== Types =====
export type RecruiterCostBasis = "hourly" | "yearly";

export interface RoiMetricsSettings {
  annualHiringTarget: number;
  timeToFillDays: number;
  recruiterHourlyCostUsd: number;
  recruiterCostBasis: RecruiterCostBasis;
}

export interface RoiMetricsInput {
  resumeScreeningsCount: number;
  chatMessagesCount: number;
  phoneInterviewMinutes: number;
  evaluationReportsCount: number;
}

export interface RoiMetricsCalculated {
  manualTimeEliminatedHours: number;
  hiringCapacityFte: number;
  hiringCostSavedUsd: number;
}

// ===== RoiMetrics Class =====
export class RoiMetrics {
  private static toDateKey(date: Date): string {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  private static normalizeWeekKey(weekStart: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(weekStart)) {
      return weekStart;
    }
    const prefix = weekStart.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(prefix)) {
      return prefix;
    }
    const parsed = new Date(weekStart);
    if (Number.isNaN(parsed.getTime())) {
      return weekStart;
    }
    return this.toDateKey(parsed);
  }

  private static toWeekStart(date: Date): Date {
    const normalized = new Date(date);
    normalized.setUTCHours(0, 0, 0, 0);
    const day = normalized.getUTCDay();
    const diffToMonday = (day + 6) % 7;
    normalized.setUTCDate(normalized.getUTCDate() - diffToMonday);
    return normalized;
  }

  private static formatChartDateLabel(input: string | Date): string {
    const date = typeof input === "string" ? new Date(input) : input;
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(date);
  }

  /**
   * Extract metrics input from StatisticsResponse
   */
  static fromStatistics(statistics: StatisticsResponse): RoiMetricsInput {
    return {
      resumeScreeningsCount: statistics.resume_count?.total ?? 0,
      chatMessagesCount: statistics.message_count?.total ?? 0,
      phoneInterviewMinutes: statistics.call_duration?.total ?? 0,
      evaluationReportsCount: statistics.call_count?.total ?? 0,
    };
  }

  /**
   * Calculate manual time eliminated in hours
   *
   * Formula:
   * (resume_count × 5min + chat_count × 3min + phone_minutes × 1.5 + eval_count × 20min) / 60
   */
  static computeManualTimeHours(input: RoiMetricsInput): number {
    const {
      MINUTES_PER_RESUME_SCREENING,
      MINUTES_PER_CHAT_MESSAGE,
      PHONE_INTERVIEW_TIME_MULTIPLIER,
      MINUTES_PER_EVALUATION_REPORT,
    } = ROI_ASSUMPTIONS;

    const totalMinutes =
      input.resumeScreeningsCount * MINUTES_PER_RESUME_SCREENING +
      input.chatMessagesCount * MINUTES_PER_CHAT_MESSAGE +
      input.phoneInterviewMinutes * PHONE_INTERVIEW_TIME_MULTIPLIER +
      input.evaluationReportsCount * MINUTES_PER_EVALUATION_REPORT;

    return totalMinutes / 60;
  }

  /**
   * Compute weekly manual-time hours from weekly raw stats detail.
   */
  static computeManualTimeHoursFromWeeklyDetail(
    detail: WeeklyStatisticsDetail,
  ): number {
    return this.computeManualTimeHours({
      resumeScreeningsCount: detail.resume_count,
      chatMessagesCount: detail.message_count,
      phoneInterviewMinutes: detail.call_duration_minutes,
      evaluationReportsCount: detail.call_count,
    });
  }

  /**
   * Calculate hiring capacity in FTE
   *
   * Formula: manual_time_hours / prorated_hours_per_fte_period
   */
  static computeHiringCapacityFte(
    manualTimeHours: number,
    period: StatisticsDateFilters,
    orgStartDate?: Date,
  ): number {
    const hoursPerFte = getProratedHoursPerFte(
      period,
      new Date(),
      orgStartDate,
    );
    return manualTimeHours / hoursPerFte;
  }

  /**
   * Calculate hiring cost saved in USD
   *
   * Formula: manual_time_hours × recruiter_hourly_cost
   */
  static computeCostSavedUsd(
    manualTimeHours: number,
    hourlyRateUsd: number,
  ): number {
    const rate =
      hourlyRateUsd || ROI_ASSUMPTIONS.DEFAULT_RECRUITER_HOURLY_RATE_USD;
    return manualTimeHours * rate;
  }

  /**
   * Compute weekly manual-time series from weekly details.
   */
  static computeWeeklyManualTimeSeries(
    details: WeeklyStatisticsDetail[],
  ): number[] {
    return details.map((detail) =>
      this.computeManualTimeHoursFromWeeklyDetail(detail),
    );
  }

  /**
   * Compute weekly cost-saved series from weekly manual-time hours.
   */
  static computeWeeklyCostSavedSeries(
    weeklyManualHours: number[],
    hourlyRateUsd: number,
  ): number[] {
    return weeklyManualHours.map((hours) =>
      this.computeCostSavedUsd(hours, hourlyRateUsd),
    );
  }

  /**
   * Fill missing weekly rows in the requested range with zeros.
   */
  static fillMissingWeeklyDetails(
    details: WeeklyStatisticsDetail[],
    afterIso?: string | null,
    beforeIso?: string | null,
  ): WeeklyStatisticsDetail[] {
    if (!afterIso || !beforeIso) {
      return [...details].sort((a, b) =>
        a.week_start.localeCompare(b.week_start),
      );
    }

    const start = this.toWeekStart(new Date(afterIso));
    const end = this.toWeekStart(new Date(beforeIso));
    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime()) ||
      start > end
    ) {
      return [...details].sort((a, b) =>
        a.week_start.localeCompare(b.week_start),
      );
    }

    const byWeek = new Map(
      details.map((detail) => [
        this.normalizeWeekKey(detail.week_start),
        detail,
      ]),
    );
    const filled: WeeklyStatisticsDetail[] = [];

    for (
      let cursor = new Date(start);
      cursor <= end;
      cursor.setUTCDate(cursor.getUTCDate() + 7)
    ) {
      const weekStart = this.toDateKey(cursor);
      const existing = byWeek.get(weekStart);
      filled.push(
        existing ?? {
          week_start: weekStart,
          call_count: 0,
          call_duration_minutes: 0,
          resume_count: 0,
          message_count: 0,
        },
      );
    }

    return filled;
  }

  /**
   * Build x-axis labels for chart start/end.
   */
  static buildChartEdgeLabels(
    details: WeeklyStatisticsDetail[],
    afterIso?: string | null,
    beforeIso?: string | null,
  ): { startLabel: string; endLabel: string } {
    if (afterIso && beforeIso) {
      return {
        startLabel: this.formatChartDateLabel(afterIso),
        endLabel: this.formatChartDateLabel(beforeIso),
      };
    }

    if (details.length > 0) {
      return {
        startLabel: this.formatChartDateLabel(details[0].week_start),
        endLabel: this.formatChartDateLabel(
          details[details.length - 1].week_start,
        ),
      };
    }

    return { startLabel: "", endLabel: "" };
  }

  /**
   * Normalize a series to 0-100 for chart rendering.
   */
  static normalizeSeriesToPercentage(values: number[]): number[] {
    if (values.length === 0) return [];
    const maxValue = Math.max(...values);
    if (maxValue <= 0) return values.map(() => 0);
    return values.map((value) => (value / maxValue) * 100);
  }

  /**
   * Convert a series to cumulative values.
   */
  static computeCumulativeSeries(values: number[]): number[] {
    let runningTotal = 0;
    return values.map((value) => {
      runningTotal += value;
      return runningTotal;
    });
  }

  /**
   * Return the latest value from a time series.
   */
  static getLatestValue(values: number[]): number | undefined {
    return values.length > 0 ? values[values.length - 1] : undefined;
  }

  /**
   * Get value at hover index, or latest if index is undefined.
   */
  static getValueAtIndex(
    values: number[],
    index: number | undefined,
  ): number | undefined {
    if (values.length === 0) return undefined;
    if (index === undefined) return this.getLatestValue(values);
    const safeIndex = Math.max(0, Math.min(index, values.length - 1));
    return values[safeIndex];
  }

  /**
   * Compute week-over-week percentage change from a series.
   */
  static computeWeekOverWeekChangePercent(
    values: number[],
  ): number | undefined {
    if (values.length < 2) return undefined;
    const previous = values[values.length - 2];
    const latest = values[values.length - 1];
    if (previous === 0) return undefined;
    return Math.round(((latest - previous) / previous) * 100);
  }

  /**
   * Compute percentage change at a given index (or latest).
   */
  static computeChangePercentAtIndex(
    values: number[],
    index: number | undefined,
  ): number | undefined {
    if (values.length < 2) return undefined;
    const targetIndex =
      index === undefined
        ? values.length - 1
        : Math.max(0, Math.min(index, values.length - 1));
    if (targetIndex < 1) return undefined;
    const previous = values[targetIndex - 1];
    const current = values[targetIndex];
    if (previous === 0) return undefined;
    return Math.round(((current - previous) / previous) * 100);
  }

  static formatHoursValue(value: number | undefined): string | undefined {
    if (value === undefined) return undefined;
    return `${value.toFixed(1)} hours`;
  }

  static formatUsdValue(value: number | undefined): string | undefined {
    if (value === undefined) return undefined;
    return `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }

  /**
   * Calculate all ROI metrics
   */
  static calculate(
    statistics: StatisticsResponse,
    settings: RoiMetricsSettings,
    period: StatisticsDateFilters,
    orgStartDate?: Date,
  ): RoiMetricsCalculated {
    const input = this.fromStatistics(statistics);
    const manualTimeEliminatedHours = this.computeManualTimeHours(input);
    const hiringCapacityFte = this.computeHiringCapacityFte(
      manualTimeEliminatedHours,
      period,
      orgStartDate,
    );
    const hiringCostSavedUsd = this.computeCostSavedUsd(
      manualTimeEliminatedHours,
      settings.recruiterHourlyCostUsd,
    );

    return {
      manualTimeEliminatedHours,
      hiringCapacityFte,
      hiringCostSavedUsd,
    };
  }

  /**
   * Generate tooltip formula string for Manual Time Eliminated (legacy, unused after structured tooltip)
   */
  static getManualTimeFormula(input: RoiMetricsInput, hours: number): string {
    const {
      MINUTES_PER_RESUME_SCREENING,
      MINUTES_PER_CHAT_MESSAGE,
      PHONE_INTERVIEW_TIME_MULTIPLIER,
      MINUTES_PER_EVALUATION_REPORT,
    } = ROI_ASSUMPTIONS;

    return (
      `(${input.resumeScreeningsCount}×${MINUTES_PER_RESUME_SCREENING}min + ` +
      `${input.chatMessagesCount}×${MINUTES_PER_CHAT_MESSAGE}min + ` +
      `${input.phoneInterviewMinutes.toFixed(0)}min×${PHONE_INTERVIEW_TIME_MULTIPLIER} + ` +
      `${input.evaluationReportsCount}×${MINUTES_PER_EVALUATION_REPORT}min) ÷ 60 = ${hours.toFixed(1)} hours`
    );
  }

  /**
   * Build full tooltip content for Hiring time saved (plain text breakdown).
   */
  static buildManualTimeTooltipContent(
    input: RoiMetricsInput,
    totalHours: number,
    agentName: string,
  ): string {
    const {
      MINUTES_PER_RESUME_SCREENING,
      MINUTES_PER_CHAT_MESSAGE,
      PHONE_INTERVIEW_TIME_MULTIPLIER,
      MINUTES_PER_EVALUATION_REPORT,
    } = ROI_ASSUMPTIONS;

    const resumeSubtotal =
      input.resumeScreeningsCount * MINUTES_PER_RESUME_SCREENING;
    const chatSubtotal = input.chatMessagesCount * MINUTES_PER_CHAT_MESSAGE;
    const interviewSubtotal =
      input.phoneInterviewMinutes * PHONE_INTERVIEW_TIME_MULTIPLIER;
    const reportSubtotal =
      input.evaluationReportsCount * MINUTES_PER_EVALUATION_REPORT;
    const grandTotalMin =
      resumeSubtotal + chatSubtotal + interviewSubtotal + reportSubtotal;

    const lines = [
      `How ${agentName} (AI agent) saved your team's time:`,
      "",
      `Resume screening (${input.resumeScreeningsCount} resumes × 5 min each) = ${resumeSubtotal} min`,
      `Scheduling messages (${input.chatMessagesCount} messages × 3 min each) = ${chatSubtotal} min`,
      `Phone interviews (${Math.round(input.phoneInterviewMinutes)} min × 1.5 incl. prep time) = ${Math.round(interviewSubtotal)} min`,
      `Evaluation reports (${input.evaluationReportsCount} reports × 20 min each) = ${reportSubtotal} min`,
      "",
      `Total (${grandTotalMin} min ÷ 60) = ${totalHours.toFixed(1)} hours`,
    ];
    return lines.join("\n");
  }

  /**
   * Build full tooltip content for Hiring cost saved.
   */
  static buildCostSavedTooltipContent(
    totalHours: number,
    hourlyRate: number,
    costSaved: number,
    agentName: string,
  ): string {
    const lines = [
      `Estimated cost your team avoided, based on time saved by ${agentName} (AI agent) and your configured hourly rate.`,
      "",
      "How it's calculated:",
      "",
      `Time saved (${totalHours.toFixed(1)} hrs) × Your recruiter's hourly rate ($${hourlyRate}/hr) = $${costSaved.toLocaleString("en-US", { maximumFractionDigits: 0 })}`,
      "",
      "To update the hourly rate, go to Dashboard Settings (top right).",
    ];
    return lines.join("\n");
  }

  /**
   * Build full tooltip content for Recruiter workload covered.
   */
  static buildHiringCapacityTooltipContent(
    totalHours: number,
    periodWorkHours: number,
    fteValue: number,
    ftePercent: number,
    agentName: string,
    recruiterHourlyCost: number,
  ): string {
    const annualSalaryEquiv =
      fteValue * recruiterHourlyCost * ROI_ASSUMPTIONS.ANNUAL_WORKING_HOURS;
    const formattedSalary = `$${annualSalaryEquiv.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
    const formattedPeriodHours = periodWorkHours.toLocaleString("en-US", {
      maximumFractionDigits: 0,
    });

    const formulaLine =
      fteValue >= 1.0
        ? `Time saved (${totalHours.toFixed(1)} hrs) ÷ Period work hours (${formattedPeriodHours} hrs) = ${fteValue.toFixed(1)} FTE`
        : `Time saved (${totalHours.toFixed(1)} hrs) ÷ Period work hours (${formattedPeriodHours} hrs) × 100 = ${ftePercent}%`;

    const lines = [
      `The ${fteValue >= 1.0 ? "amount" : "percentage"} of a full-time recruiter's workload that ${agentName} (AI agent) handled for you during this period.`,
      "",
      "How it's calculated:",
      "",
      formulaLine,
      "",
      `That's equivalent to approximately ${formattedSalary}/yr in recruiter salary.`,
    ];
    return lines.join("\n");
  }

  /**
   * Generate tooltip formula string for Hiring Capacity (legacy, unused after structured tooltip)
   */
  static getHiringCapacityFormula(
    hours: number,
    period: StatisticsDateFilters,
    fte: number,
  ): string {
    const hoursPerFte = getProratedHoursPerFte(period);
    return `${hours.toFixed(1)} hours ÷ ${Math.round(hoursPerFte)} hours/FTE = ${fte.toFixed(2)} FTE`;
  }

  /**
   * Generate tooltip formula string for Cost Saved (legacy, unused after structured tooltip)
   */
  static getCostSavedFormula(
    hours: number,
    hourlyRate: number,
    costSaved: number,
  ): string {
    return `${hours.toFixed(1)} hours × $${hourlyRate}/hr = $${costSaved.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }

  /**
   * Default settings
   */
  /**
   * Convert yearly salary to hourly rate
   */
  static yearlyToHourly(yearly: number): number {
    return yearly / ROI_ASSUMPTIONS.ANNUAL_WORKING_HOURS;
  }

  /**
   * Convert hourly rate to yearly salary
   */
  static hourlyToYearly(hourly: number): number {
    return hourly * ROI_ASSUMPTIONS.ANNUAL_WORKING_HOURS;
  }

  static getDefaultSettings(): RoiMetricsSettings {
    return {
      annualHiringTarget: 60,
      timeToFillDays: 60,
      recruiterHourlyCostUsd: 130,
      recruiterCostBasis: "hourly",
    };
  }

  /**
   * Org-wide settings from the ROI-metrics-settings endpoint. Null while unloaded or no member has
   * saved the settings yet (the GET returns null in that case).
   */
  static settingsFromResponse(
    response: RoiMetricsSettingsResponse | null | undefined,
  ): RoiMetricsSettings | null {
    if (response == null) {
      return null;
    }
    return {
      annualHiringTarget: response.annual_hiring_target,
      timeToFillDays: response.time_to_fill_days,
      recruiterHourlyCostUsd: response.recruiter_hourly_cost_usd,
      recruiterCostBasis: response.recruiter_cost_basis as RecruiterCostBasis,
    };
  }

  static toSettingsRequest(
    settings: RoiMetricsSettings,
  ): RoiMetricsSettingsRequest {
    return {
      annual_hiring_target: settings.annualHiringTarget,
      time_to_fill_days: settings.timeToFillDays,
      recruiter_hourly_cost_usd: settings.recruiterHourlyCostUsd,
      recruiter_cost_basis: settings.recruiterCostBasis,
    };
  }
}
